import { observer } from "mobx-react-lite";
import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Form, Input, message, Modal, Select, Spin } from "antd";
import { LevelSettings } from "./EditOrAddTest";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "../../stores/root-store-context";
import { Test, TestAnswer, TestAttempt, TestQuestion, UserTestAssignment } from "../../stores/TestsStore";
import Layout from "../../components/layout/Layout";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { TypeTestQuestion } from "../../enums/TypeTestQuestion";

interface QuestionForm {
    questionName: string;
    correctAnswer: string;
}

export const UserAnswersTest = observer(({ }: {}) => {
    const { t } = useTranslation();
    const [existAttempt, setExistAttempt] = useState<TestAttempt>();
    const [existAnswer, setExistAnswer] = useState<TestAnswer[]>([]);
    const [existTest, setExistTest] = useState<Test>();
    const { id } = useParams();
    const { testsStore, applicationStore } = useStore();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const API_AI_KEY = `${process.env.REACT_APP_AI_GEMINI_API_KEY}`;
    const [existQuestions, setExistQuestions] = useState<TestQuestion[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            const existTestAttempt = await testsStore.getTestAttemptByIdIncludeAll(id ?? "0");
            const existTest = await testsStore.getTestById(existTestAttempt.testId.toString());
            const allAnswers = existTestAttempt?.testAnswers || [];
            const testQuestionIds = (existTest?.testQuestions || []).map(question => question.id);

            const validAnswers = allAnswers.filter(answer => answer.testQuestionId !== null);
            const answeredQuestionIds = validAnswers.map(answer => answer.testQuestionId) as number[];
            const relatedQuestions = existTest?.testQuestions?.filter(
                question => answeredQuestionIds.includes(question.id)
            ) || [];

            const questionsWithoutCorrectAnswer = relatedQuestions.filter(
                q => !q.correctAnswer || q.correctAnswer === ''
            );

            const answersWithoutQuestion = allAnswers.filter(
                a => (a.testQuestionId === null && a.questionName) ||
                    (a.testQuestionId !== null && !testQuestionIds.includes(a.testQuestionId ?? 0) && a.questionName)
            );

            if (questionsWithoutCorrectAnswer.length > 0 || answersWithoutQuestion.length > 0) {
                const generatedAnswers = await handleGetCorrectAnswerForQuestion(
                    questionsWithoutCorrectAnswer,
                    answersWithoutQuestion
                );

                const updatedQuestions = relatedQuestions.map(q => {
                    const generated = generatedAnswers.find(g => g.questionName == q.name);
                    return generated ? { ...q, correctAnswer: generated.correctAnswer } : q;
                });

                const updatedAnswers = allAnswers.map(a => {
                    const generated = generatedAnswers.find(g => g.questionName == a.questionName);
                    return generated ? { ...a, correctAnswer: generated.correctAnswer } : a;
                });

                setExistQuestions(updatedQuestions);
                setExistAnswer(updatedAnswers);
            } else {
                setExistQuestions(relatedQuestions);
                setExistAnswer(allAnswers);
            }

            setExistAttempt(existTestAttempt);
            setExistTest(existTest);
            setLoading(false);
        };

        fetchData();
    }, []);

    const handleGetCorrectAnswerForQuestion = async (
        questions: TestQuestion[],
        answersWithoutQuestion: TestAnswer[]
    ) => {
        const questionNamesFromQuestions = questions.map(q => q.name);
        const questionNamesFromAnswers = answersWithoutQuestion.map(a => a.questionName);
        const allQuestionNames = Array.from(new Set(questionNamesFromQuestions.concat(questionNamesFromAnswers)));

        const requestGenerateQuestions = `
            Generate correct answers for the following questions in JSON format.
            For each question name, provide a correct answer based on what the question might be asking.
            Return an array of objects with "questionName" and "correctAnswer" fields.
            Question names: ${allQuestionNames.join(', ')}
            
            Example output:
            [
                {"questionName": "What is 2+2", "correctAnswer": "4"},
                {"questionName": "Capital of France", "correctAnswer": "Paris"}
            ]
        `;

        const genAI = new GoogleGenerativeAI(API_AI_KEY);
        const model = genAI.getGenerativeModel({
            model: "gemini-2.0-flash",
            generationConfig: {
                responseMimeType: "application/json"
            }
        });

        const result = await model.generateContent(requestGenerateQuestions);
        let rawResponse = result.response.text().trim();

        rawResponse = rawResponse
            .replace(/```json/g, '')
            .replace(/```/g, '')
            .trim();

        const jsonStart = rawResponse.indexOf("[");
        const jsonEnd = rawResponse.lastIndexOf("]") + 1;

        if (jsonStart === -1 || jsonEnd === 0) {
            throw new Error("Response does not contain a valid JSON array: " + rawResponse);
        }

        const cleanedResponse = rawResponse.substring(jsonStart, jsonEnd);

        try {
            const parsed = JSON.parse(cleanedResponse) as QuestionForm[];
            return parsed;
        } catch (error) {
            console.error("Parsing error details:", error);
            throw new Error(`Failed to parse generated questions: ${cleanedResponse}`);
        }
    };

    const getSelectedOptions = (questionId: number) => {
        const answers = existAnswer?.find(answer => answer.testQuestionId === questionId)?.answer;
        return answers ? answers.split(',').map(optionId => Number(optionId.trim())) : [];
    };

    const getAnswerForQuestion = (questionId: number) => {
        const answer = existAnswer?.find(answer => answer.testQuestionId === questionId);
        return answer ? answer : null;
    };

    const toggleAnswerCorrectByQuestionId = (questionId: number) => {
        setExistAnswer(prevAnswers =>
            prevAnswers?.map(answer =>
                answer.testQuestionId == questionId
                    ? { ...answer, isCorrect: !answer.isCorrect }
                    : answer
            )
        );
    };

    const toggleAnswerCorrectById = (answerId: number) => {
        setExistAnswer(prevAnswers =>
            prevAnswers?.map(answer =>
                answer.id == answerId
                    ? { ...answer, isCorrect: !answer.isCorrect }
                    : answer
            )
        );
    };

    const updateTestAttempt = async () => {

        setLoading(true);
        if (!existAttempt || !existTest) return;

        const maxPossibleScore = existAnswer.reduce((total, answer) => {
            return total + answer.numberPoints;
        }, 0);

        let obtainedScore = 0;
        existAnswer?.forEach(answer => {
            if (answer.isCorrect) {
                obtainedScore += answer.numberPoints;
            }
        });

        const newPercentage = Math.round(maxPossibleScore > 0 ? (obtainedScore / maxPossibleScore) * 100 : 0);

        const updatedAttempt = {
            ...existAttempt,
            percentage: newPercentage,
            testAnswers: existAnswer ?? []
        };

        try {
            const result = await testsStore.updateTestAttempt(existAttempt.id, updatedAttempt);

            if (result) {
                message.success(t("Success"));
                setExistAttempt(updatedAttempt);
            } else {
                message.error(t("Error"));
            }
        } catch (e) {
            console.error(e);
            message.error(t("Error"));
        }
        finally {
            setLoading(false);
        }
    };

    return (
        <Layout headerStyle={1} footerStyle={2} breadcrumbTitle={t("tests.result_test")}>
            <Spin spinning={loading} style={{ minHeight: 600 }}>
                <div style={{ maxWidth: "800px", margin: "0 auto", padding: "20px" }}>

                    {existAttempt && (
                        <div style={{ marginBottom: "20px", padding: "15px", border: "1px solid #e0e0e0", borderRadius: "8px", backgroundColor: "#f9f9f9", position: "relative" }}>
                            {isEditing ? (
                                <SaveOutlined
                                    style={{ position: "absolute", top: "10px", right: "10px", cursor: "pointer", fontSize: "20px" }}
                                    onClick={() => {
                                        updateTestAttempt();
                                        setIsEditing(false);
                                    }}
                                />
                            ) : (
                                <EditOutlined
                                    style={{ position: "absolute", top: "10px", right: "10px", cursor: "pointer", fontSize: "20px" }}
                                    onClick={() => setIsEditing(true)}
                                />
                            )}
                            <p><strong>{t("tests.testName")}:</strong> {existTest?.name}</p>
                            <p><strong>{t("tests.attemptNumber")}:</strong> {existAttempt.numberAttempt}</p>
                            <p><strong>{t("tests.grade")}:</strong> {existAttempt.percentage}%</p>
                            <p><strong>{t("tests.Result")}: </strong>
                                {(existAttempt.percentage ?? 0) > 80
                                    ? t("passage_test.test_passed")
                                    : t("passage_test.test_failed")}
                            </p>
                            <p><strong>{t("Start Date")}:</strong> {new Date(existAttempt.startDate).toLocaleString('uk-UA', {
                                day: 'numeric',
                                month: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                            }).replace(/,/g, '')}</p>
                            <p><strong>{t("tests.finishDate")}:</strong> {new Date(existAttempt.finishDate).toLocaleString('uk-UA', {
                                day: 'numeric',
                                month: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                            }).replace(/,/g, '')}</p>
                            <p><strong>{t("tests.timeSpent")}:</strong> {existAttempt.timeSpentMinutes ? `${existAttempt.timeSpentMinutes} ${t("add_test.minutes")}` : `1 ${t("add_test.minutes")}`}</p>
                        </div>
                    )}

                    {existQuestions?.map((question, index) => {
                        const userAnswer = getAnswerForQuestion(question.id);

                        return (
                            <div
                                key={index}
                                style={{
                                    marginBottom: "30px",
                                    padding: "20px",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "8px",
                                    backgroundColor: "#fafafa",
                                    position: "relative",
                                }}
                            >
                                <div style={{ marginBottom: "10px", fontWeight: "bold" }}>
                                    {`${t("add_test.Question")} ${index + 1}: ${question.name}`}
                                </div>

                                {(question.typeTestQuestionId === TypeTestQuestion.SingleChoice ||
                                    question.typeTestQuestionId === TypeTestQuestion.MultipleChoice) && (
                                        <div style={{ marginTop: "15px" }}>
                                            {question.testOptions?.map((option, optionIndex) => {
                                                const isSelected = getSelectedOptions(question.id).includes(option.id);
                                                const isCorrect = option.isCorrect;

                                                return (
                                                    <div
                                                        key={optionIndex}
                                                        style={{
                                                            marginBottom: "5px",
                                                            padding: "10px",
                                                            border: "1px solid",
                                                            borderColor: isSelected
                                                                ? isCorrect
                                                                    ? "#28a745"
                                                                    : "#dc3545"
                                                                : "#e0e0e0",
                                                            backgroundColor: isSelected
                                                                ? isCorrect
                                                                    ? "#d4edda"
                                                                    : "#f8d7da"
                                                                : "#f9f9f9",
                                                            borderRadius: "4px",
                                                            position: "relative",
                                                        }}
                                                    >
                                                        {`${t("add_test.Option")} ${optionIndex + 1}: ${option.name}`}
                                                        {isCorrect && (
                                                            <span style={{ marginLeft: "10px", color: "#28a745" }}>
                                                                ({t("tests.correct")})
                                                            </span>
                                                        )}
                                                        {isSelected && !isCorrect && (
                                                            <span style={{ marginLeft: "10px", color: "#dc3545" }}>
                                                                ({t("tests.selected_incorrect")})
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {isEditing ? (
                                                <Checkbox
                                                    checked={userAnswer?.isCorrect}
                                                    style={{
                                                        position: "absolute",
                                                        top: "10px",
                                                        right: "10px",
                                                        cursor: "pointer",
                                                    }}
                                                    onChange={() => toggleAnswerCorrectByQuestionId(question.id)}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: "10px",
                                                        right: "10px",
                                                        width: "20px",
                                                        height: "20px",
                                                        backgroundColor: userAnswer?.isCorrect ? "#28a745" : "#dc3545",
                                                        borderRadius: "50%",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        color: "#fff",
                                                    }}
                                                >
                                                    {userAnswer?.isCorrect ? "✔" : "✘"}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                {question.typeTestQuestionId === TypeTestQuestion.WriteCode && (
                                    <div>
                                        <div style={{ marginTop: "10px" }}>
                                            <strong>{t("add_test.WriteCodePrompt")}:</strong>
                                            <pre style={{ backgroundColor: "#f4f4f4", padding: "10px", borderRadius: "4px" }}>
                                                {userAnswer?.answer || t("tests.NoAnswer")}
                                            </pre>
                                        </div>
                                        <div style={{ marginTop: "10px" }}>
                                            <strong>{t("add_test.CorrectAnswer")}:</strong>
                                            <pre style={{ backgroundColor: "#f4f4f4", padding: "10px", borderRadius: "4px" }}>
                                                {question.correctAnswer}
                                            </pre>
                                        </div>
                                        {isEditing ? (
                                            <Checkbox
                                                checked={userAnswer?.isCorrect}
                                                style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    right: "10px",
                                                    cursor: "pointer",
                                                }}
                                                onChange={() => toggleAnswerCorrectByQuestionId(question.id)}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    right: "10px",
                                                    width: "20px",
                                                    height: "20px",
                                                    backgroundColor: userAnswer?.isCorrect ? "#28a745" : "#dc3545",
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    color: "#fff",
                                                }}
                                            >
                                                {userAnswer?.isCorrect ? "✔" : "✘"}
                                            </div>
                                        )
                                        }

                                    </div>
                                )}

                                {question.typeTestQuestionId === TypeTestQuestion.OpenAnswer && (
                                    <div>
                                        <div style={{ marginTop: "10px" }}>
                                            <strong>{t("add_test.WriteOpenAnswer")}:</strong>
                                            <p>{userAnswer?.answer || t("tests.NoAnswer")}</p>
                                        </div>
                                        <div style={{ marginTop: "10px" }}>
                                            <strong>{t("add_test.CorrectAnswer")}:</strong>
                                            <pre style={{ backgroundColor: "#f4f4f4", padding: "10px", borderRadius: "4px" }}>
                                                {question.correctAnswer}
                                            </pre>
                                        </div>
                                        {isEditing ? (
                                            <Checkbox
                                                checked={userAnswer?.isCorrect}
                                                style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    right: "10px",
                                                    cursor: "pointer",
                                                }}
                                                onChange={() => toggleAnswerCorrectByQuestionId(question.id)}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    right: "10px",
                                                    width: "20px",
                                                    height: "20px",
                                                    backgroundColor: userAnswer?.isCorrect ? "#28a745" : "#dc3545",
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    color: "#fff",
                                                }}
                                            >
                                                {userAnswer?.isCorrect ? "✔" : "✘"}
                                            </div>
                                        )
                                        }
                                    </div>
                                )}

                                {question.typeTestQuestionId === TypeTestQuestion.FixCode && (
                                    <div>
                                        <div style={{ marginTop: "10px" }}>
                                            <strong>{t("add_test.Code")}:</strong>
                                            <pre style={{ backgroundColor: "#f4f4f4", padding: "10px", borderRadius: "4px" }}>
                                                {userAnswer?.answer || t("tests.NoAnswer")}
                                            </pre>
                                        </div>
                                        <div style={{ marginTop: "10px" }}>
                                            <strong>{t("add_test.CorrectAnswer")}:</strong>
                                            <pre style={{ backgroundColor: "#f4f4f4", padding: "10px", borderRadius: "4px" }}>
                                                {question.correctAnswer}
                                            </pre>
                                        </div>
                                        {isEditing ? (
                                            <Checkbox
                                                checked={userAnswer?.isCorrect}
                                                style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    right: "10px",
                                                    cursor: "pointer",
                                                }}
                                                onChange={() => toggleAnswerCorrectByQuestionId(question.id)}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    right: "10px",
                                                    width: "20px",
                                                    height: "20px",
                                                    backgroundColor: userAnswer?.isCorrect ? "#28a745" : "#dc3545",
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    color: "#fff",
                                                }}
                                            >
                                                {userAnswer?.isCorrect ? "✔" : "✘"}
                                            </div>
                                        )
                                        }
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {existAnswer
                        ?.filter((answer) =>
                            !existTest?.testQuestions?.some((question) => question.id === answer.testQuestionId)
                        )
                        .map((answer, index) => (
                            <div
                                key={`unmatched-${index}`}
                                style={{
                                    marginBottom: "30px",
                                    padding: "20px",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "8px",
                                    backgroundColor: "#fafafa",
                                    position: "relative",
                                }}
                            >
                                <div style={{ marginBottom: "10px", fontWeight: "bold" }}>
                                    {`${t("add_test.Question")}: ${answer.questionName}`}
                                </div>
                                <div style={{ marginTop: "10px" }}>
                                    <strong>{t("add_test.UserAnswer")}:</strong>
                                    <p>{answer.answerTextName || t("tests.NoAnswer")}</p>
                                </div>
                                <div style={{ marginTop: "10px" }}>
                                    <strong>{t("add_test.CorrectAnswer")}:</strong>
                                    <pre style={{ backgroundColor: "#f4f4f4", padding: "10px", borderRadius: "4px" }}>
                                        {answer.correctAnswer}
                                    </pre>
                                </div>
                                {isEditing ? (
                                    <Checkbox
                                        checked={answer.isCorrect}
                                        style={{
                                            position: "absolute",
                                            top: "10px",
                                            right: "10px",
                                            cursor: "pointer",
                                        }}
                                        onChange={() => toggleAnswerCorrectById(answer.id)}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "10px",
                                            right: "10px",
                                            width: "20px",
                                            height: "20px",
                                            backgroundColor: answer.isCorrect ? "#28a745" : "#dc3545",
                                            borderRadius: "50%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            color: "#fff",
                                        }}
                                    >
                                        {answer.isCorrect ? "✔" : "✘"}
                                    </div>
                                )
                                }
                            </div>
                        ))}

                    <div style={{ textAlign: "center" }}>
                        <Button style={{ marginBottom: 20 }} type="primary" onClick={() => navigate(`/${applicationStore.company.Tenant}/tests`)}>
                            {t("passage_test.back_to_tests")}
                        </Button>
                    </div>
                </div>
            </Spin>
        </Layout>
    );
});